import { BaseEditViewStore, BaasicDropdownStore } from 'core/stores';
import { PatientInformationBarNewFlowForm } from 'application/facility-order-new-flow/forms';
import { PatientService } from "application/patient/services";
import { action, observable } from "mobx";
import moment from 'moment';
import { ModalParams } from 'core/models';

class PatientInformationBarNewFlowViewStore extends BaseEditViewStore {
    @observable accessor isStatusBarVisible = false;
    @observable accessor isEditMode = false;
    @observable accessor isEditOptionDisabled = false;
    constructor(rootStore, componentProps) {
        super(rootStore, {
            name: 'patient-information-bar-new-flow',
            Form: PatientInformationBarNewFlowForm,
            authorization: 'portalSection.update',
            FormClass: PatientInformationBarNewFlowForm,
            autoInit: false
        });

        this.rootStore = rootStore;
        this.patient = componentProps.patient;
        this.oldPatient = { ...componentProps.patient };
        // this.facilityOrderNewFlowConfirmModal = componentProps.facilityOrderNewFlowConfirmModal;
        this.brightreeBaseUrl = rootStore.applicationStore.app.baasic.settings.brightreeBaseUrl;
        this.role = componentProps.role;
        this.isNewPatient = componentProps.isNewPatient;
        this.isMasterPatient = componentProps.isMasterPatient;
        this.facilityOrder = componentProps.facilityOrder;
        this.isStatusBarVisible = this.role === "Initial Intake" || this.role === "Insurance";

        this.genderStore = new BaasicDropdownStore({
            textField: 'name',
            dataItemKey: 'name',
            filterable: true
        }, {
            fetchFunc: () => {
                return [{ id: 1, name: 'Male' }, { id: 2, name: 'Female' }];
            }
        });
        this.facilityOrderNewFlowConfirmModal = new ModalParams({});
        this.patientService = new PatientService(rootStore.application.baasic.apiClient);

        this.form.$("firstName").value = this.patient.firstName;
        this.form.$("lastName").value = this.patient.lastName;
        this.form.$("gender").value = this.patient.gender;
        this.form.$("ssn").value = this.patient.ssn;
        this.form.$("dateOfBirth").value = this.patient.dateOfBirth ? this.formatDate(this.patient.dateOfBirth) : "";

        this.isEditOptionDisabled = this.getDisabledRules(this.facilityOrder.facilityOrderStatuses);
    }

    formatDate(dateOfBirth) {
        const date = new Date(dateOfBirth);
        date.setHours(date.getHours() + 7);
        const day = date.getUTCDate();
        const month = date.getUTCMonth() + 1;
        const year = date.getUTCFullYear().toString();
        return `${month}/${day}/${year}`;
    }

    @action.bound
    changeMenuVisibility() {
        this.isStatusBarVisible = !this.isStatusBarVisible;
        if (!this.isStatusBarVisible)
            this.isEditMode = false;

        if (this.isStatusBarVisible)
            this.isEditOptionDisabled = this.getDisabledRules();
    }

    @action.bound
    changeEditMode(isCancel = false) {
        this.isEditMode = !this.isEditMode;
        if (isCancel) {
            this.form.$("firstName").value = this.oldPatient.firstName;
            this.form.$("lastName").value = this.oldPatient.lastName;
            this.form.$("gender").value = this.oldPatient.gender;
            this.form.$("ssn").value = this.oldPatient.ssn;
            this.form.$("dateOfBirth").value = this.oldPatient.dateOfBirth ? this.formatDate(this.oldPatient.dateOfBirth) : "";
        }
    }

    @action.bound
    isFormValidate() {
        var result = true;
        if (this.form.$("firstName").value == "" || this.form.$("lastName").value == "") {
            this.rootStore.notificationStore.error('Patient first and last name cannot be empty.');
            result = false;
        }

        let regex = /^\d{9}$/;
        if (this.form.$("ssn").value != "" && !regex.test(this.form.$("ssn").value)) {
            this.rootStore.notificationStore.error('SSN must contain 9 digits.');
            result = false;
        }

        regex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12][0-9]|3[01])\/(18|19|20)\d{2}$/;
        if (this.form.$("dateOfBirth").value == "" || !regex.test(this.form.$("dateOfBirth").value)) {
            this.rootStore.notificationStore.error('DOB format must be mm/dd/yyyy.');
            result = false;
        }
        return result;
    }

    @action.bound
    async openFacilityOrderNewFlowConfirmModal() {
        if (!this.isFormValidate()) {
            return;
        }

        await this.facilityOrderNewFlowConfirmModal.open({
            onConfirm: async () => await this.save(),
            message: "Changes made to patient info will be inputted to Brightree. Please confirm all information is accurate before proceeding."
        });
    }

    @action.bound
    async save() {
        this.changeEditMode();
        try {
            await this.patientService.updatePatientDetails({
                patientBrightreeId: this.patient.patientKey,
                height: this.patient.height,
                weight: this.patient.weight,
                firstName: this.form.$("firstName").value,
                lastName: this.form.$("lastName").value,
                gender: this.form.$("gender").value,
                ssn: this.form.$("ssn").value,
                dateOfBirth: moment(this.form.$("dateOfBirth").value, "MM/DD/YYYY").set({ hour: 12, minute: 0, second: 0, millisecond: 0 }).format("YYYY-MM-DD HH:mm:ss"),
            });

            this.oldPatient.firstName = this.form.$("firstName").value;
            this.oldPatient.lastName = this.form.$("lastName").value;
            this.oldPatient.gender = this.form.$("gender").value;
            this.oldPatient.ssn = this.form.$("ssn").value;
            this.oldPatient.dateOfBirth = this.form.$("dateOfBirth").value ? this.formatDate(this.patient.dateOfBirth) : "";

            if (this.patient.firstName != this.form.$("firstName").value) {
                this.patient.firstName = this.form.$("firstName").value;
            }
            if (this.patient.lastName != this.form.$("lastName").value) {
                this.patient.lastName = this.form.$("lastName").value;
            }
        }
        catch (err) {
            this.changeEditMode();
            this.rootStore.notificationStore.error((err && err.data) || "Error occurred while trying to update patient information in Brightree.");
        }
    }

    @action.bound
    goToBT() {
        if(this.isEditMode)
            return;
        
        let url = `${this.brightreeBaseUrl}Patient/frmPatientPersonalRO.aspx?PatientKey=${this.patient.patientKey}&Edit=0`;
        window.open(`${url}`);
    }

    getDisabledRules() {
        let initialIntakeStatus = this.facilityOrder.facilityOrderStatuses.find(x => x.status && x.status.abrv === "initial-intake");
        let insuranceReviewStatus = this.facilityOrder.facilityOrderStatuses.find(x => x.status && x.status.abrv === "insurance-review");
        let usIntakeStatus = this.facilityOrder.facilityOrderStatuses.find(x => x.status && x.status.abrv === "us-intake");
        let clinicalReviewStatus = this.facilityOrder.facilityOrderStatuses.find(x => x.status && x.status.abrv === "clinical-review");

        if (this.role === "Initial Intake") {
            if (!initialIntakeStatus) {
                if (insuranceReviewStatus || usIntakeStatus || clinicalReviewStatus) {
                    return true;
                }
                return true;
            } else if (initialIntakeStatus.completedOn) {
                return true;
            } else if (insuranceReviewStatus || usIntakeStatus || clinicalReviewStatus) {
                return true;
            } else {
                return false;
            }
        }
        else if (this.role === "Insurance") {
            if (!insuranceReviewStatus) {
                if (usIntakeStatus || clinicalReviewStatus) {
                    return true;
                }
                return true;
            } else if (insuranceReviewStatus.completedOn) {
                return true;
            } else if (usIntakeStatus || clinicalReviewStatus) {
                return true;
            } else {
                return false;
            }
        }
        else if (this.role === "US Intake") {
            if (usIntakeStatus) {
                return false;
            }
            return true;
        }
        else if (this.role === "Clinical") {
            if (clinicalReviewStatus) {
                return false;
            }
            return true;
        }
    }
}

export default PatientInformationBarNewFlowViewStore;
